import React, { useEffect, useState } from "react";
import OfferManager from "../../services/dashboard/OfferManager";
import { format } from "date-fns";
import {
  CheckCircleIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
import { Edit2, Trash2, Calendar, Percent, Tag } from "lucide-react";

const AdminOfferManager = () => {
  const [offers, setOffers] = useState([]);
  const [form, setForm] = useState({
    id: null,
    offerTitle: "",
    offerDescription: "",
    discountPercentage: "",
    applicableSubscriptionType: "ALL",
    startDate: "",
    endDate: "",
    isActive: true,
  });

  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchOffers();
  }, []);

  const formatDate = (date) => {
    let parsed;

    if (Array.isArray(date)) {
      const [y, m, d, hh = 0, mm = 0, ss = 0] = date;
      parsed = new Date(y, m - 1, d, hh, mm, ss);
    } else {
      parsed = new Date(date);
    }

    if (isNaN(parsed)) return "N/A";

    return parsed.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const fetchOffers = async () => {
    try {
      const response = await OfferManager.getAllOffers();
      const offersArray = response.responseData || [];

      const normalizedOffers = offersArray.map((offer) => ({
        ...offer,
        startDateArray: offer.startDate,
        endDateArray: offer.endDate,
        startDateInput: toInputDate(offer.startDate),
        endDateInput: toInputDate(offer.endDate),
      }));

      setOffers(normalizedOffers);
    } catch (err) {
      console.error("Error fetching offers", err);
    }
  };

  const toInputDate = (dateInput) => {
    if (!dateInput) return "";

    if (Array.isArray(dateInput)) {
      const [y, m, d, hh = 0, mm = 0, ss = 0] = dateInput;
      const date = new Date(y, m - 1, d, hh, mm, ss);
      return date.toLocaleDateString("sv-SE");
    }

    const date = new Date(dateInput);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString("sv-SE");
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        startDate: new Date(form.startDate).toISOString(),
        endDate: new Date(form.endDate).toISOString(),
      };

      if (isEditing) {
        await OfferManager.updateOffer(form.id, payload);
      } else {
        await OfferManager.createOffer(payload);
      }

      resetForm();
      fetchOffers();
    } catch (err) {
      console.error("Error saving offer", err);
    }
  };

  const handleEdit = (offer) => {
    setForm({
      id: offer.id,
      offerTitle: offer.offerTitle,
      offerDescription: offer.offerDescription,
      discountPercentage: offer.discountPercentage,
      applicableSubscriptionType: offer.applicableSubscriptionType || "ALL",
      startDate: toInputDate(offer.startDate),
      endDate: toInputDate(offer.endDate),
      isActive: offer.isActive ?? true,
    });
    setIsEditing(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this offer?")) {
      try {
        await OfferManager.deleteOffer(id);
        fetchOffers();
      } catch (err) {
        console.error("Error deleting offer", err);
      }
    }
  };

  const resetForm = () => {
    setForm({
      id: null,
      offerTitle: "",
      offerDescription: "",
      discountPercentage: "",
      applicableSubscriptionType: "ALL",
      startDate: "",
      endDate: "",
      isActive: true,
    });
    setIsEditing(false);
  };

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-950 min-h-screen">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
              Offer Management
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Create and manage promotional offers and discounts
            </p>
          </div>
          {isEditing && (
            <button
              onClick={resetForm}
              className="px-5 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 font-medium"
            >
              Cancel Edit
            </button>
          )}
        </header>

        {/* Form - Structured Sections */}
        <form
          onSubmit={handleSubmit}
          className="bg-white dark:bg-gray-900/60 rounded-xl shadow-sm border border-gray-200/60 dark:border-gray-800/80 overflow-hidden"
        >
          <div className="p-6 space-y-6">
            {/* Section 1: Offer Basics */}
            <div className="space-y-3">
              <h3 className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-[0.15em]">
                Offer Basics
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Offer Title
                  </label>
                  <input
                    type="text"
                    name="offerTitle"
                    value={form.offerTitle}
                    onChange={handleChange}
                    placeholder="e.g., Summer Sale 2026"
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800/60 border border-gray-200/80 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-blue-200/60 dark:focus:ring-blue-800/40 focus:border-blue-400 dark:focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Discount Percentage
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="discountPercentage"
                      value={form.discountPercentage}
                      onChange={handleChange}
                      placeholder="0"
                      min="0"
                      max="100"
                      className="w-full px-4 py-2 pr-11 bg-gray-50 dark:bg-gray-800/60 border border-gray-200/80 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-blue-200/60 dark:focus:ring-blue-800/40 focus:border-blue-400 dark:focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500"
                      required
                    />
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-sm font-medium text-gray-400 dark:text-gray-500 pointer-events-none">
                      %
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200/80 dark:border-gray-800/80" />

            {/* Section 2: Applicability */}
            <div className="space-y-3">
              <h3 className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-[0.15em]">
                Applicability
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Applicable To
                  </label>
                  <select
                    name="applicableSubscriptionType"
                    value={form.applicableSubscriptionType}
                    onChange={handleChange}
                    className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800/60 border border-gray-200/80 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-blue-200/60 dark:focus:ring-blue-800/40 focus:border-blue-400 dark:focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-gray-100"
                    required
                  >
                    <option value="ALL">All Subscription Types</option>
                    <option value="NEW">New Subscriptions Only</option>
                    <option value="RENEW">Renewals Only</option>
                    <option value="CHANGE_PLAN">Plan Changes Only</option>
                  </select>
                </div>
                <div className="flex items-end pb-0.5">
                  <label className="flex items-center gap-3 cursor-pointer group">
                    <div className="relative">
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={form.isActive}
                        onChange={handleChange}
                        className="sr-only peer"
                      />
                      <div className="w-12 h-6 bg-gray-200 dark:bg-gray-700 rounded-full peer peer-checked:bg-green-500 dark:peer-checked:bg-green-600 transition-all duration-300 shadow-inner"></div>
                      <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full transition-all duration-300 peer-checked:translate-x-6 shadow-md"></div>
                    </div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Active Offer
                    </span>
                  </label>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200/80 dark:border-gray-800/80" />

            {/* Section 3: Duration */}
            <div className="space-y-3">
              <h3 className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-[0.15em]">
                Duration
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    Start Date
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      name="startDate"
                      value={form.startDate}
                      onChange={handleChange}
                      className="w-full px-4 py-2 pl-10 bg-gray-50 dark:bg-gray-800/60 border border-gray-200/80 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-blue-200/60 dark:focus:ring-blue-800/40 focus:border-blue-400 dark:focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-gray-100"
                      required
                    />
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none">
                      <Calendar size={16} />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                    End Date
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      name="endDate"
                      value={form.endDate}
                      onChange={handleChange}
                      className="w-full px-4 py-2 pl-10 bg-gray-50 dark:bg-gray-800/60 border border-gray-200/80 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-blue-200/60 dark:focus:ring-blue-800/40 focus:border-blue-400 dark:focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-gray-100"
                      required
                    />
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500 pointer-events-none">
                      <Calendar size={16} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-gray-200/80 dark:border-gray-800/80" />

            {/* Section 4: Description */}
            <div className="space-y-3">
              <h3 className="text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-[0.15em]">
                Description
              </h3>
              <textarea
                name="offerDescription"
                value={form.offerDescription}
                onChange={handleChange}
                placeholder="Describe the offer details and terms..."
                rows={2}
                className="w-full px-4 py-2 bg-gray-50 dark:bg-gray-800/60 border border-gray-200/80 dark:border-gray-700/80 rounded-lg focus:ring-2 focus:ring-blue-200/60 dark:focus:ring-blue-800/40 focus:border-blue-400 dark:focus:border-blue-500 transition-all duration-200 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 resize-none"
              />
            </div>
          </div>

          {/* Footer with Submit Button */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-800/40 border-t border-gray-200/80 dark:border-gray-800/80 flex justify-end gap-3">
            {isEditing && (
              <button
                type="button"
                onClick={resetForm}
                className="px-5 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-all duration-200 font-medium"
              >
                Cancel
              </button>
            )}
            <button
              type="submit"
              className="px-9 py-3 bg-orange-600 hover:bg-orange-700 dark:bg-orange-500 dark:hover:bg-orange-600 text-white rounded-lg transition-all duration-200 font-semibold shadow-md hover:shadow-lg hover:-translate-y-0.5 text-[15px]"
            >
              {isEditing ? "Update Offer" : "Publish Offer"}
            </button>
          </div>
        </form>

        {/* Active Offers - Card List */}
        <div>
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-4">
            Active Offers ({offers.length})
          </h2>

          {offers.length === 0 ? (
            <div className="bg-white dark:bg-gray-900/60 rounded-xl border border-gray-200/60 dark:border-gray-800/80 p-16 text-center">
              <div className="flex flex-col items-center gap-3">
                <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full">
                  <Tag className="w-8 h-8 text-gray-400 dark:text-gray-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-1">
                    No offers yet
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Create your first offer to get started
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {offers.map((offer) => (
                <div
                  key={offer.id}
                  className="group bg-white dark:bg-gray-900/60 rounded-xl border border-gray-200/80 dark:border-gray-800/80 hover:border-gray-300/80 dark:hover:border-gray-700/80 hover:shadow-lg transition-all duration-200 p-5"
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* Left: Title + Description */}
                    <div className="flex-1 min-w-0 space-y-3">
                      <div className="flex items-start gap-3">
                        <h3 className="text-[15px] font-semibold text-gray-950 dark:text-gray-50 leading-tight">
                          {offer.offerTitle}
                        </h3>
                        <span className="inline-flex items-center px-3 py-1 rounded-lg bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 text-sm font-bold border border-orange-200/80 dark:border-orange-800/80 shadow-sm">
                          {offer.discountPercentage}% OFF
                        </span>
                      </div>
                      
                      {offer.offerDescription && (
                        <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2 leading-relaxed">
                          {offer.offerDescription}
                        </p>
                      )}

                      {/* Tags Row */}
                      <div className="flex flex-wrap items-center gap-2 pt-1">
                        {/* Status Badge */}
                        <span
                          className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px] font-semibold border shadow-sm ${
                            offer.isActive
                              ? "bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border-green-200/80 dark:border-green-800/80"
                              : "bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border-gray-200/80 dark:border-gray-700/80"
                          }`}
                        >
                          {offer.isActive ? (
                            <>
                              <CheckCircleIcon className="w-3 h-3" />
                              Active
                            </>
                          ) : (
                            <>
                              <XCircleIcon className="w-3 h-3" />
                              Inactive
                            </>
                          )}
                        </span>

                        {/* Applicable Type Badge */}
                        <span className="inline-flex items-center px-2.5 py-1 rounded-md bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 text-[10px] font-semibold border border-blue-200/80 dark:border-blue-800/80 shadow-sm">
                          {offer.applicableSubscriptionType === "ALL"
                            ? "All Types"
                            : offer.applicableSubscriptionType === "NEW"
                            ? "New Only"
                            : offer.applicableSubscriptionType === "RENEW"
                            ? "Renewals"
                            : offer.applicableSubscriptionType === "CHANGE_PLAN"
                            ? "Plan Changes"
                            : offer.applicableSubscriptionType || "All Types"}
                        </span>

                        {/* Auto Apply Badge */}
                        {offer.applyAutomatically && (
                          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 text-[10px] font-semibold border border-purple-200/80 dark:border-purple-800/80 shadow-sm">
                            <CheckCircleIcon className="w-3 h-3" />
                            Auto Apply
                          </span>
                        )}

                        {/* Date Range */}
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-[10px] font-medium border border-gray-200/80 dark:border-gray-700/80">
                          <Calendar size={10} />
                          {formatDate(offer.startDate)} - {formatDate(offer.endDate)}
                        </span>
                      </div>
                    </div>

                    {/* Right: Actions */}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                      <button
                        onClick={() => handleEdit(offer)}
                        className="p-2 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 text-gray-400 hover:text-blue-600 dark:text-gray-500 dark:hover:text-blue-400 transition-all duration-150 hover:scale-110"
                        title="Edit offer"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(offer.id)}
                        className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 hover:text-red-600 dark:text-gray-500 dark:hover:text-red-400 transition-all duration-150 hover:scale-110"
                        title="Delete offer"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminOfferManager;
